import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Table, Button, Input, Space, Typography, App, Modal, Switch, type InputRef } from 'antd';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { PlusOutlined, SearchOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined, ExclamationCircleOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { FiEdit2 } from 'react-icons/fi';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/api';
import { theme } from '../../theme';
import { ExportExcelButton, ExportPDFButton, PageTitle, StyledCard, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import AltaPadron from './AltaPadron';
import { EditarPadron } from './EditarPadron';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text } = Typography;

interface PadronData {
  option: number;
  id: number;
  nc: string;
  denominacion: string;
  direccion: string;
  provincia_nombre: string;
  localidad_nombre: string;
  ndocumento: string;
  actividad_nombre: string;
  alta: number;
}

const ListadoPadron: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [data, setData] = useState<PadronData[]>([]);
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const [userData, setUserData] = useState<any>(null);
  const searchInput = useRef<InputRef>(null);
  const [globalSearch, setGlobalSearch] = useState<string>('');
  const [showStatsExpanded, setShowStatsExpanded] = useState(false);
  const [modalAltaOpen, setModalAltaOpen] = useState(false);
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = [
    'id',
    'nc',
    'denominacion',
    'direccion',
    'provincia',
    'localidad',
    'ndocumento',
    'actividad',
    'alta',
    'acciones'
  ];

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
    'id': 'ID',
    'nc': 'N° Cliente',
    'denominacion': 'Razón Social',
    'direccion': 'Dirección',
    'provincia': 'Provincia',
    'localidad': 'Localidad',
    'ndocumento': 'N° Documento',
    'actividad': 'Perfil',
    'alta': 'Estado',
    'acciones': 'Acciones'
  };

  useEffect(() => {
    if (user) {
      console.log('Usuario autenticado:', user);
      setUserData({
        id_empresa: user.id_empresa,
        id_sucursal: user.id_sucursal
      });
    }
  }, [user]);

  // Cargar todos los datos al iniciar
  useEffect(() => {
    if (userData?.id_empresa) {
      fetchPadron();
    }
  }, [userData?.id_empresa]);

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'padron',
    allKeys: allColumnKeys,
    requiredKeys: ['acciones'],
  });

  const fetchPadron = async () => {
    if (!userData?.id_empresa) {
      console.log('No hay datos de empresa disponibles');
      return;
    }

    setLoading(true);
    
    try {
      const token = await getAccessTokenSilently();
      
      // Cargar todos los datos sin filtros (se filtrarán en la tabla)
      const params = new URLSearchParams();
      
      const url = `${process.env.REACT_APP_PADRON_URL}/buscar_padron/${userData.id_empresa}/?${params.toString()}`;
      console.log('Realizando búsqueda con URL:', url);

      const response = await apiClient.get<PadronData[]>(url, {
        token,
        timeout: 10000,
      });

      console.log('Datos recibidos:', response.data);
      setData(response.data);
    } catch (error: any) {
      console.error('Error al cargar el padrón:', error);
      if (error.code === 'ECONNABORTED') {
        message.error('Timeout: La petición tardó demasiado');
      } else if (error.response?.status === 404) {
        message.error('No se encontraron datos para esta empresa');
      } else {
        message.error(`Error al cargar el padrón: ${error.message || 'Error desconocido'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Función helper para búsqueda en columnas
  const getColumnSearchProps = (dataIndex: keyof PadronData, placeholder: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
      const handleSearch = () => {
        confirm();
      };

      const handleReset = () => {
        clearFilters();
        confirm();
      };

      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={placeholder}
            value={selectedKeys[0]}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onPressEnter={handleSearch}
            style={{ 
              display: 'block',
              width: 188,
              marginBottom: 8
            }}
          />
          <Space>
            <Button
              type="primary"
              onClick={handleSearch}
              icon={<SearchOutlined />}
              size="small"
              style={{ 
                width: 90,
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary
              }}
            >
              Buscar
            </Button>
            <Button
              onClick={handleReset}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? theme.colors.secondary : theme.colors.textSecondary }} />
    ),
    onFilter: (value: any, record: PadronData) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes(String(value).toLowerCase()),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: '#fff3cd', 
          padding: '2px 4px',
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    if (!globalSearch.trim()) {
      return data;
    }

    const searchLower = globalSearch.toLowerCase();
    return data.filter(record => {
      return (
        record.id.toString().includes(searchLower) ||
        record.nc.toLowerCase().includes(searchLower) ||
        record.denominacion.toLowerCase().includes(searchLower) ||
        record.direccion.toLowerCase().includes(searchLower) ||
        record.provincia_nombre.toLowerCase().includes(searchLower) ||
        record.localidad_nombre.toLowerCase().includes(searchLower) ||
        record.ndocumento.toLowerCase().includes(searchLower) ||
        record.actividad_nombre.toLowerCase().includes(searchLower) ||
        (record.alta === 1 ? 'activo' : 'inactivo').includes(searchLower)
      );
    });
  }, [data, globalSearch]);

  // Calcular estadísticas para el resumen
  const estadisticas = useMemo(() => {
    const activos = filteredData.filter(r => r.alta === 1).length;
    const inactivos = filteredData.filter(r => r.alta === 0).length;
    
    // Top 3 provincias
    const provinciasCount = filteredData.reduce((acc, record) => {
      const prov = record.provincia_nombre || 'Sin provincia';
      acc[prov] = (acc[prov] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
    const topProvincias = Object.entries(provinciasCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([nombre, count]) => ({ nombre, count }));

    // Último registro agregado (asumiendo que el ID más alto es el más reciente)
    const ultimoRegistro = filteredData.length > 0 
      ? filteredData.reduce((max, record) => record.id > max.id ? record : max, filteredData[0])
      : null;

    return {
      total: filteredData.length,
      activos,
      inactivos,
      topProvincias,
      ultimoRegistro
    };
  }, [filteredData]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => ({
          'ID': record.id,
          'N° Cliente': record.nc,
          'Razón Social': record.denominacion,
          'Dirección': record.direccion,
          'Provincia': record.provincia_nombre,
          'Localidad': record.localidad_nombre,
          'N° Documento': record.ndocumento,
          'Perfil': record.actividad_nombre,
          'Estado': record.alta === 1 ? 'Activo' : 'Inactivo'
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Padrón');
      XLSX.writeFile(workbook, `padron_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      message.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Listado de Padrón', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const tableData = filteredData.map(record => [
        record.id.toString(),
        record.nc,
        record.denominacion.substring(0, 30),
        record.provincia_nombre.substring(0, 15),
        record.localidad_nombre.substring(0, 20),
        record.alta === 1 ? 'Activo' : 'Inactivo'
      ]);

      (doc as any).autoTable({
        head: [['ID', 'N° Cliente', 'Razón Social', 'Provincia', 'Localidad', 'Estado']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`padron_${new Date().toISOString().split('T')[0]}.pdf`);
      message.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      message.error('Error al exportar el archivo PDF');
    }
  };

  const columns = [
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>N° Cliente</span>,
      dataIndex: 'nc',
      key: 'nc',
      width: 120,
      align: 'center' as const,
      sorter: (a: PadronData, b: PadronData) => a.nc.localeCompare(b.nc),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Razón Social</span>,
      dataIndex: 'denominacion',
      key: 'denominacion',
      sorter: (a: PadronData, b: PadronData) => a.denominacion.localeCompare(b.denominacion),
      ...getColumnSearchProps('denominacion', 'Buscar razón social...'),
      render: (text: string) => (
        <span style={{ color: theme.colors.textPrimary, fontWeight: 500 }}>
          {globalSearch ? highlightText(text, globalSearch) : text}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Dirección</span>,
      dataIndex: 'direccion',
      key: 'direccion',
      render: (text: string) => (
        <span style={{ color: theme.colors.textSecondary }}>
          {globalSearch ? highlightText(text, globalSearch) : text}
        </span>
      ),
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Provincia</span>,
      dataIndex: 'provincia_nombre',
      key: 'provincia',
      sorter: (a: PadronData, b: PadronData) => a.provincia_nombre.localeCompare(b.provincia_nombre),
      filters: Array.from(new Set(data.map(item => item.provincia_nombre)))
        .filter(val => val)
        .map(val => ({ text: val, value: val })),
      onFilter: (value: any, record: PadronData) => record.provincia_nombre === value,
      filterSearch: true,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Localidad</span>,
      dataIndex: 'localidad_nombre',
      key: 'localidad',
      sorter: (a: PadronData, b: PadronData) => a.localidad_nombre.localeCompare(b.localidad_nombre),
      filters: Array.from(new Set(data.map(item => item.localidad_nombre)))
        .filter(val => val)
        .map(val => ({ text: val, value: val })),
      onFilter: (value: any, record: PadronData) => record.localidad_nombre === value,
      filterSearch: true,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>N° Documento</span>,
      dataIndex: 'ndocumento',
      key: 'ndocumento',
      align: 'center' as const,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Perfil</span>,
      dataIndex: 'actividad_nombre',
      key: 'actividad',
      sorter: (a: PadronData, b: PadronData) => a.actividad_nombre.localeCompare(b.actividad_nombre),
      filters: Array.from(new Set(data.map(item => item.actividad_nombre)))
        .filter(val => val)
        .map(val => ({ text: val, value: val })),
      onFilter: (value: any, record: PadronData) => record.actividad_nombre === value,
      filterSearch: true,
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Estado</span>,
      dataIndex: 'alta',
      key: 'alta',
      align: 'center' as const,
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value: any, record: PadronData) => record.alta === value,
      render: (alta: number, record: PadronData) => {
        const isActivo = alta === 1;
        return (
          <Switch
            checked={isActivo}
            onChange={(checked) => handleEliminar(record.id, checked)}
            checkedChildren="Activo"
            unCheckedChildren="Inactivo"
            size="small"
          />
        );
      },
    },
    {
      title: <span style={{ color: theme.colors.textPrimary, fontWeight: 600 }}>Acciones</span>,
      key: 'acciones',
      align: 'center' as const,
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: PadronData) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button
            onClick={() => {
              setEditingId(record.id);
              setModalEditarOpen(true);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary,
              fontSize: '18px',
              padding: '4px 8px',
              display: 'flex',
              alignItems: 'center'
            }}
            title="Editar registro"
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primaryDark}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primary}
          >
            {React.createElement(FiEdit2 as any)}
          </button>
        </div>
      ),
    },
  ];

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
    const columnsByKey = new Map(columns.map(col => [col.key as string, col]));
    return columnManager.orderedVisibleKeys
      .map(key => columnsByKey.get(key))
      .filter((col): col is (typeof columns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, columns]);

  const handleNuevo = () => {
    setModalAltaOpen(true);
  };

  const handleModalAltaCancel = () => {
    setModalAltaOpen(false);
  };

  const handleModalAltaSuccess = () => {
    setModalAltaOpen(false);
    // Refrescar el listado
    fetchPadron();
  };

  const handleModalEditarCancel = () => {
    setModalEditarOpen(false);
    setEditingId(null);
  };

  const handleModalEditarSuccess = () => {
    setModalEditarOpen(false);
    setEditingId(null);
    // Refrescar el listado
    fetchPadron();
  };

  const handleEliminar = async (id: number, checked: boolean) => {
    if (!userData?.id_empresa) {
      message.error('No se encontraron datos de empresa');
      return;
    }

    // Buscar el registro completo para obtener todos los datos necesarios
    const registro = data.find(r => r.id === id);
    if (!registro) {
      message.error('No se encontró el registro');
      return;
    }

    // Actualizar estado optimísticamente
    const previousAlta = registro.alta;
    setData(prev => prev.map(r => r.id === id ? { ...r, alta: checked ? 1 : 0 } : r));

    try {
      const token = await getAccessTokenSilently();
      
      // Obtener los datos completos del registro para actualizarlo
      const obtenerResponse = await apiClient.get(
        `${process.env.REACT_APP_PADRON_URL}/obtener_padron/${userData.id_empresa}/${id}`,
        { token }
      );

      const padronData = obtenerResponse.data;
      
      // Preparar datos para actualización
      const datosActualizacion: any = {
        denominacion: padronData.denominacion || registro.denominacion,
        direccion: padronData.direccion || registro.direccion,
        provincia_id: padronData.provincia_id,
        localidad_id: padronData.localidad_id,
        departamento_id: padronData.departamento_id,
        tipo_documento_id: padronData.tipo_documento_id,
        ndocumento: padronData.ndocumento || registro.ndocumento,
        nro_telefono: padronData.nro_telefono || padronData.telefono || '',
        email: padronData.email || '',
        categoria_iva_id: padronData.categoria_iva_id,
        actividad_id: padronData.actividad_id,
        alta: checked ? 1 : 0 // Cambiar estado según el Switch
      };

      // Actualizar el registro
      await apiClient.post(
        `${process.env.REACT_APP_PADRON_URL}/actualizar_padron/${userData.id_empresa}/${id}`,
        datosActualizacion,
        { token }
      );
      
      message.success(checked ? 'Registro activado correctamente' : 'Registro desactivado correctamente');
      // No recargar toda la página, solo mantener el estado actualizado
    } catch (error: any) {
      console.error('Error al cambiar estado:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error al cambiar el estado del registro';
      message.error(errorMessage);
      // Revertir el estado en caso de error
      setData(prev => prev.map(r => r.id === id ? { ...r, alta: previousAlta } : r));
    }
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título y estadísticas compactas */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '12px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <PageTitle style={{ marginBottom: 0, fontSize: '24px' }}>Padrón</PageTitle>

        {/* Estadísticas compactas en la barra superior */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '6px',
            border: `1px solid ${theme.colors.borderLight}`
          }}>
            <UserOutlined style={{ fontSize: '14px', color: theme.colors.primary }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>Total:</Text>
            <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary }}>
              {estadisticas.total}
            </Text>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            backgroundColor: '#f6ffed',
            borderRadius: theme.borderRadius.md,
            border: `1px solid #b7eb8f`
          }}>
            <CheckCircleOutlined style={{ fontSize: '14px', color: theme.colors.success }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>Activos:</Text>
            <Text strong style={{ fontSize: '12px', color: theme.colors.success }}>
              {estadisticas.activos}
            </Text>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            backgroundColor: '#fff1f0',
            borderRadius: theme.borderRadius.md,
            border: `1px solid #ffccc7`
          }}>
            <CloseCircleOutlined style={{ fontSize: '14px', color: theme.colors.error }} />
            <Text type="secondary" style={{ fontSize: '11px' }}>Inactivos:</Text>
            <Text strong style={{ fontSize: '12px', color: theme.colors.error }}>
              {estadisticas.inactivos}
            </Text>
          </div>

          <Button
            type="text"
            size="small"
            icon={showStatsExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setShowStatsExpanded(!showStatsExpanded)}
            style={{
              fontSize: '12px',
              color: theme.colors.textSecondary
            }}
          >
            {showStatsExpanded ? 'Ocultar detalles' : 'Ver más'}
          </Button>
        </div>
      </div>

      {/* Cards de resumen expandidas (colapsables) */}
      {showStatsExpanded && (
        <div style={{ 
          display: 'grid', 
          gap: '16px', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          marginBottom: '24px',
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <StyledCard 
            style={{ 
              borderTop: `4px solid ${theme.colors.primary}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%'
            }}
            bodyStyle={{ padding: '12px', minHeight: '70px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', wordBreak: 'break-word' }}>Total Registros</Text>
                <div style={{ margin: '6px 0 0 0', color: theme.colors.textPrimary, wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '18px', fontWeight: 600 }}>
                  {estadisticas.total.toLocaleString('es-AR')}
                </div>
              </div>
              <UserOutlined style={{ fontSize: '24px', color: theme.colors.primary, flexShrink: 0 }} />
            </div>
          </StyledCard>

          <StyledCard 
            style={{ 
              borderTop: `4px solid ${theme.colors.success}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%'
            }}
            bodyStyle={{ padding: '12px', minHeight: '70px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', wordBreak: 'break-word' }}>Activos</Text>
                <div style={{ margin: '6px 0 0 0', color: theme.colors.success, wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '18px', fontWeight: 600 }}>
                  {estadisticas.activos.toLocaleString('es-AR')}
                </div>
              </div>
              <CheckCircleOutlined style={{ fontSize: '24px', color: theme.colors.success, flexShrink: 0 }} />
            </div>
          </StyledCard>

          <StyledCard 
            style={{ 
              borderTop: `4px solid ${theme.colors.error}`,
              minWidth: '150px',
              flex: '1 1 auto',
              height: '100%'
            }}
            bodyStyle={{ padding: '12px', minHeight: '70px' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', wordBreak: 'break-word' }}>Inactivos</Text>
                <div style={{ margin: '6px 0 0 0', color: theme.colors.error, wordBreak: 'break-word', overflowWrap: 'break-word', fontSize: '18px', fontWeight: 600 }}>
                  {estadisticas.inactivos.toLocaleString('es-AR')}
                </div>
              </div>
              <CloseCircleOutlined style={{ fontSize: '24px', color: theme.colors.error, flexShrink: 0 }} />
            </div>
          </StyledCard>

          {estadisticas.topProvincias.length > 0 && (
            <StyledCard 
              style={{ 
                borderTop: `4px solid ${theme.colors.secondary}`
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                    Top Provincia
                  </Text>
                  <div style={{ margin: '6px 0 0 0', color: theme.colors.textPrimary, fontSize: '14px', fontWeight: 600 }}>
                    {estadisticas.topProvincias[0]?.nombre || 'N/A'}
                  </div>
                  <Text style={{ fontSize: '11px', color: theme.colors.textSecondary }}>
                    {estadisticas.topProvincias[0]?.count || 0} registros
                  </Text>
                </div>
                <EnvironmentOutlined style={{ fontSize: '24px', color: theme.colors.secondary }} />
              </div>
            </StyledCard>
          )}
        </div>
      )}
      
      <StyledCard 
        title="Listado de Padrón"
      >
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Barra de búsqueda y acciones */}
        <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', justifyContent: 'space-between', flexWrap: 'wrap', alignItems: 'center' }}>
          <Input
            placeholder="Buscar en todos los campos..."
            prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            allowClear
            style={{
              maxWidth: '400px',
              flex: 1,
              minWidth: '250px',
              fontSize: '13px',
              height: '32px'
            }}
          />
          <Space>
            <ColumnManagerDropdown
              manager={columnManager}
              labels={columnNames}
              requiredKeys={['acciones']}
              buttonSize="small"
            />
            <ExportExcelButton
              onClick={handleExportExcel}
              size="small"
              disabled={filteredData.length === 0}
            />
            <ExportPDFButton
              onClick={handleExportPDF}
              size="small"
              disabled={filteredData.length === 0}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleNuevo}
              size="small"
              style={{
                backgroundColor: theme.colors.secondary,
                borderColor: theme.colors.secondary,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Nuevo
            </Button>
          </Space>
        </div>

        {/* Mensaje de resultados filtrados */}
        {globalSearch && (
          <div style={{ 
            marginBottom: '12px', 
            padding: '6px 10px', 
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: theme.styles.borderRadius,
            fontSize: '12px',
            color: theme.colors.textSecondary
          }}>
            Mostrando {filteredData.length} de {data.length} registros
            {globalSearch && ` que coinciden con "${globalSearch}"`}
          </div>
        )}

        <Table
          columns={filteredColumns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          scroll={{ x: 1300, y: 'max(150px, calc(100vh - 400px))' }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} registros`,
            position: ['bottomRight']
          }}
          size="small"
          onRow={(record) => ({
            onMouseEnter: (e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '#f8f9fa';
            },
            onMouseLeave: (e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '';
            },
            style: {
              transition: 'background-color 0.2s ease'
            }
          })}
        />
      </div>
      </StyledCard>

      {/* Modal para Alta de Padrón */}
      <Modal
        title="Nuevo Registro de Padrón"
        open={modalAltaOpen}
        onCancel={handleModalAltaCancel}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        destroyOnClose
      >
        <AltaPadron
          asModal={true}
          open={modalAltaOpen}
          onCancel={handleModalAltaCancel}
          onSuccess={handleModalAltaSuccess}
        />
      </Modal>

      {/* Modal para Editar Padrón */}
      {editingId && (
        <Modal
          title="Editar Registro de Padrón"
          open={modalEditarOpen}
          onCancel={handleModalEditarCancel}
          footer={null}
          width={1200}
          style={{ top: 20 }}
          destroyOnClose
        >
          <EditarPadron
            id={editingId}
            asModal={true}
            open={modalEditarOpen}
            onCancel={handleModalEditarCancel}
            onSuccess={handleModalEditarSuccess}
          />
        </Modal>
      )}
    </div>
  );
};

export default ListadoPadron;
